import { useFormContext } from 'components/ReactHookForm';
import type { Ticket } from 'models/api';
import type { FieldDependency, FormInput } from 'models/sitecore';
import { filterObject } from 'utils/collection';
import { is } from 'utils/helpers';

export const WORKSITE_FIELD_NAME = 'worksite';

/**
 * Check if a section response is a ticket with a client action.
 *
 * Section endpoints are set in Sitecore and not all of them return a ticket.
 */
export function isTicket(value: unknown): value is Ticket {
	return is.object(value) && 'clientAction' in value;
}

export const getValuesForSubmit = (
	formInputs: FormInput[],
	formValues: Record<string, unknown>,
): Record<string, unknown> =>
	filterObject(formValues, (_val, key) =>
		Boolean(formInputs.find(({ name }) => name === key)?.useData),
	);

function parseFieldDependency(requires: FieldDependency) {
	// Uses index of, to not split the value into possible parts.
	const separatorIndex = requires.indexOf(':');
	if (separatorIndex === -1) {
		return { name: requires };
	}
	return {
		name: requires.slice(0, separatorIndex),
		value: requires.slice(separatorIndex + 1),
	};
}

export function useRequiresDependencyValue(
	dependency: FieldDependency | undefined,
) {
	const { hookFormContext } = useFormContext();
	if (!dependency) {
		return true;
	}

	const { name: fieldName, value: requiredValue } =
		parseFieldDependency(dependency);

	const currentValue = hookFormContext.watch(fieldName);

	if (!requiredValue) {
		return Boolean(currentValue);
	}

	// Some kind of handling of array value fields.
	return is.array(currentValue)
		? currentValue.includes(requiredValue)
		: currentValue === requiredValue;
}
